import { format } from 'date-fns';
import { toProperCase } from '@core/utils/string';
import type { MaterialColor } from '../../api/materialColor';
import type {
  AlbItem,
  ChasubleItem,
  MitreItem,
  OrderItem,
  StoleItem,
} from '../../api/order';
import type { Size } from '../../api/size';
import type { Vestment } from '../../api/vestment';
import { AlbSubtype } from '../enum/albSubtype';
import { VestmentCategory } from '../enum/vestmentCategory';
import {
  VestmentType,
  getCategory,
  isV1VestmentType,
} from '../enum/vestmentType';
import {
  albDesignSection,
  getAlbFeatures,
  getAlbLaceOptionLabel,
  getAlbLaceStyleLabel,
  getAlbPreviewSubtype,
  getAlbSubtypeLabel,
} from './alb';
import {
  type ChasublePanelField,
  getBodyTypeLabel,
  getChasubleFeatures,
  getChasubleMaterialField,
  getChasublePanelFields,
  getGothicTypeLabel,
  getItemDesignCode,
  getPatchingTypeLabel,
  hasChasubleSleeveDesign,
  showsChasubleCollar,
  showsChasublePanel,
} from './chasuble';
import { getFullName } from './customer';
import type { JobOrder } from './jobOrderPrint';
import { formatMeasurement } from './measurement';
import { formatMaterialLabel } from './orderItem';
import {
  type SizeMeasurementField,
  getSizeFieldsForOrderItem,
  getSizeLabel,
} from './size';
import { getStoleFeatures } from './stole';
import { getVestmentImageUrl, getVestmentTypeLabel } from './vestment';

export type JobOrderField = { label: string; value: string; colorHex?: string };

export type JobOrderSection = { title: string; fields: JobOrderField[] };

export type JobOrderSpec = {
  customerName: string;
  dueDate: string;
  quantity: number;
  title: string;
  /** `null` for a newer alb type. Other vestments still print the design. */
  designCode: string | null;
  /** `null` when the vestment type takes no measurements. */
  size: { label: string; fields: JobOrderField[] } | null;
  /** Rendered as equal-width columns. */
  sections: JobOrderSection[];
  /** `null` when the vestment type has no center finish. */
  centerFinish: string | null;
  imageUrl: string | null;
  imageAlt: string;
  remarks: string;
};

export type JobOrderSpecContext = {
  sizes: Size[];
  vestments: Vestment[];
};

export const EMPTY_VALUE = '—';

const field = (label: string, value?: string | null): JobOrderField => ({
  label,
  value: value || EMPTY_VALUE,
});

const measurementField = (label: string, value?: number | null) =>
  field(label, formatMeasurement(value));

const withColorHex = (
  jobOrderField: JobOrderField,
  hexCode?: string | null
): JobOrderField => {
  const colorHex = hexCode?.trim();
  return colorHex ? { ...jobOrderField, colorHex } : jobOrderField;
};

const materialField = (label: string, material?: MaterialColor | null) =>
  withColorHex(
    field(label, material ? formatMaterialLabel(material) : null),
    material?.hexCode
  );

const fabricColorFields = (item: {
  fabric?: { name: string } | null;
  color?: { name: string; hexCode?: string | null } | null;
}): JobOrderField[] => [
  field('Fabric', item.fabric?.name),
  withColorHex(field('Color', item.color?.name), item.color?.hexCode),
];

function buildSize(item: OrderItem, sizeLabel: string): JobOrderSpec['size'] {
  const fields = getSizeFieldsForOrderItem(item);
  if (fields.length === 0) {
    return null;
  }
  const measurements = item as Partial<
    Record<SizeMeasurementField, number | null>
  >;
  return {
    label: sizeLabel,
    fields: fields.map((name) =>
      measurementField(toProperCase(name), measurements[name])
    ),
  };
}

const CHASUBLE_PANEL_FIELDS: Record<
  ChasublePanelField,
  (item: ChasubleItem) => JobOrderField[]
> = {
  fabric: (item) => [field('Fabric', item.panelFabric?.name)],
  color: (item) => [
    withColorHex(
      field('Color', item.panelColor?.name),
      item.panelColor?.hexCode
    ),
  ],
  width: (item) => [measurementField('Width', item.panelWidth)],
  length: (item) => [measurementField('Length', item.panelLength)],
  patching: (item) => {
    const materialName = getChasubleMaterialField(item.vestment?.type, item);
    return [
      field('Patching', getPatchingTypeLabel(item.patchingType)),
      ...(materialName
        ? [materialField(toProperCase(materialName), item[materialName])]
        : []),
    ];
  },
  trimming: (item) => [materialField('Trimming', item.trimming)],
  gothicType: (item) => [field('Type', getGothicTypeLabel(item.gothicType))],
  galoon: (item) => [materialField('Galoon', item.galoon)],
  dimension: (item) => [field('Dimension', item.panelDimension)],
  liningFabric: (item) => [field('Lining Fabric', item.liningFabric?.name)],
  liningColor: (item) => [
    withColorHex(
      field('Lining Color', item.liningColor?.name),
      item.liningColor?.hexCode
    ),
  ],
  stoleSize: (item) => [field('Stole Size', item.stoleSize)],
};

function buildChasubleSections(item: ChasubleItem): JobOrderSection[] {
  const vestmentType = item.vestment?.type;
  const body: JobOrderSection = {
    title: 'Body',
    fields: [
      ...(getChasubleFeatures(vestmentType)?.bodyType
        ? [field('Type', getBodyTypeLabel(item.bodyType))]
        : []),
      ...(hasChasubleSleeveDesign(vestmentType, item.bodyType)
        ? [field('Design', item.design?.code)]
        : []),
      ...fabricColorFields({
        fabric: item.bodyFabric,
        color: item.bodyColor,
      }),
    ],
  };
  const panel: JobOrderSection = {
    title: 'Panel',
    fields: getChasublePanelFields(vestmentType, item, { print: true }).flatMap(
      (name) => CHASUBLE_PANEL_FIELDS[name](item)
    ),
  };
  const collar: JobOrderSection = {
    title: 'Collar',
    fields: [
      field('Type', (item.collar ?? item.legacyCollar)?.name),
      ...fabricColorFields({
        fabric: item.collarFabric,
        color: item.collarColor,
      }),
    ],
  };

  return [
    body,
    ...(showsChasublePanel(vestmentType, { print: true }) ? [panel] : []),
    ...(showsChasubleCollar(vestmentType) ? [collar] : []),
  ];
}

function buildAlbSections(item: AlbItem): JobOrderSection[] {
  const features = getAlbFeatures(item.vestment?.type);
  const designSection = albDesignSection(item.subtype);
  const isRoman = item.vestment?.type === VestmentType.AlbV1Roman;
  const subtypeFields = features?.hasSubtypes
    ? [field('Subtype', getAlbSubtypeLabel(item.subtype))]
    : [];
  return [
    {
      title: 'Body',
      fields: [
        ...fabricColorFields({
          fabric: item.bodyFabric,
          color: item.bodyColor,
        }),
        ...(isRoman ? [field('Lace', item.withLace ? 'Yes' : 'No')] : []),
      ],
    },
    ...(subtypeFields.length > 0 || designSection?.design
      ? [
          {
            title: 'Design',
            fields: [
              ...subtypeFields,
              ...(designSection?.design
                ? [
                    field('Code', item.design?.code),
                    ...(designSection.thread
                      ? [materialField('Thread', item.thread)]
                      : []),
                  ]
                : []),
              ...(item.subtype === AlbSubtype.Lace
                ? [
                    field('Style', getAlbLaceStyleLabel(item.laceStyle)),
                    field('Lace', getAlbLaceOptionLabel(item.laceOption)),
                  ]
                : []),
            ],
          },
        ]
      : []),
    ...(features?.collar
      ? [{ title: 'Collar', fields: [field('Type', item.collar?.name)] }]
      : []),
  ];
}

function buildStoleSections(item: StoleItem): JobOrderSection[] {
  const features = getStoleFeatures(item.vestment?.type);
  return [
    {
      title: 'Panel',
      fields: [
        ...(features?.hasCuts ? [field('Type', item.stoleCut?.name)] : []),
        ...fabricColorFields({
          fabric: item.panelFabric,
          color: item.panelColor,
        }),
      ],
    },
    {
      title: 'Options',
      fields: [
        item.cord ? materialField('Cord', item.cord) : field('Cord', 'Without'),
        item.tassel
          ? materialField('Tassel', item.tassel)
          : field('Tassel', 'Without'),
        ...(features?.patching === false
          ? []
          : [
              item.patchingFabric || item.patchingColor
                ? withColorHex(
                    field(
                      'Patching',
                      `${item.patchingFabric?.name ?? EMPTY_VALUE} / ${item.patchingColor?.name ?? EMPTY_VALUE}`
                    ),
                    item.patchingColor?.hexCode
                  )
                : field('Patching', 'Without'),
            ]),
      ],
    },
  ];
}

function buildMitreSections(item: MitreItem): JobOrderSection[] {
  return [
    {
      title: 'Body',
      fields: fabricColorFields({
        fabric: item.bodyFabric,
        color: item.bodyColor,
      }),
    },
    {
      title: 'Panel',
      fields: fabricColorFields({
        fabric: item.panelFabric,
        color: item.panelColor,
      }),
    },
    {
      title: 'Lining',
      fields: fabricColorFields({
        fabric: item.liningFabric,
        color: item.liningColor,
      }),
    },
    { title: 'Tassel', fields: [materialField('Tassel', item.tassel)] },
  ];
}

function buildSections(item: OrderItem): JobOrderSection[] {
  const vestmentType = item.vestment?.type;
  if (!vestmentType) {
    return [];
  }
  switch (getCategory(vestmentType)) {
    case VestmentCategory.Chasuble:
      return buildChasubleSections(item);
    case VestmentCategory.Alb:
      return buildAlbSections(item);
    case VestmentCategory.Stole:
      return buildStoleSections(item);
    case VestmentCategory.Mitre:
      return buildMitreSections(item);
  }
}

function jobOrderTypeLabel(
  vestment?: { name?: string | null; type?: VestmentType | null } | null
): string {
  const type = vestment?.type;
  if (!type) {
    return EMPTY_VALUE;
  }
  if (!isV1VestmentType(type)) {
    return getVestmentTypeLabel(type);
  }
  const name = vestment?.name?.trim();
  if (!name) {
    return getVestmentTypeLabel(type).replace(/^Legacy /, '');
  }
  const category = toProperCase(getCategory(type));
  return name.toLowerCase().endsWith(category.toLowerCase())
    ? name
    : `${name} ${category}`;
}

export function buildJobOrderSpec(
  { order, item }: JobOrder,
  { sizes, vestments }: JobOrderSpecContext
): JobOrderSpec {
  const vestmentType = item.vestment?.type;
  const sizeLabel = getSizeLabel(item, '(adjusted)', { sizes });
  const catalogVestment = vestments.find(
    (vestment) => vestment.id === item.vestment?.id
  );
  return {
    customerName: getFullName(order.customer) || EMPTY_VALUE,
    dueDate: order.dueDate
      ? format(new Date(order.dueDate), 'MMM d, yyyy')
      : EMPTY_VALUE,
    quantity: item.quantity ?? 1,
    title: jobOrderTypeLabel(item.vestment),
    designCode:
      vestmentType &&
      getAlbFeatures(vestmentType) &&
      !isV1VestmentType(vestmentType)
        ? null
        : getItemDesignCode(item) || EMPTY_VALUE,
    size: buildSize(item, sizeLabel),
    sections: buildSections(item),
    centerFinish: getChasubleFeatures(vestmentType)?.centerFinish
      ? (formatMeasurement((item as ChasubleItem).centerFinish) ?? EMPTY_VALUE)
      : null,
    imageUrl: getVestmentImageUrl(
      item.vestment,
      (item as ChasubleItem).collar,
      (item as StoleItem).stoleCut,
      getAlbPreviewSubtype(
        catalogVestment?.albSubtypes,
        (item as AlbItem).subtype,
        (item as AlbItem).laceStyle
      )
    ),
    imageAlt: item.vestment?.name ?? '',
    remarks: [order.notes, item.notes].filter(Boolean).join('\n'),
  };
}
